/* eslint-disable react/no-unescaped-entities */

/**
 * Bannière de consentement analytics — RGPD-by-design.
 *
 * Affichée une seule fois tant que l'utilisateur n'a pas pris position.
 * Comportement :
 *   - "Accepter" → localStorage `confilex_analytics_consent=accepted`
 *     → un anon_id éphémère sera généré et joint aux événements suivants.
 *   - "Refuser" → consent=denied → les events sont quand même envoyés
 *     (compteurs anonymes) MAIS sans anon_id stable. La rétention des IP
 *     hashées rotatives empêche tout pistage transversal.
 *
 * Honore DNT (Do Not Track) — si activé, la bannière n'apparaît pas et aucun
 * événement n'est posté.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X } from "lucide-react";

const CONSENT_KEY = "confilex_analytics_consent";

function dnt() {
  if (typeof navigator === "undefined") return false;
  return navigator.doNotTrack === "1" || navigator.doNotTrack === "yes" || window.doNotTrack === "1";
}

export default function AnalyticsConsentBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (dnt()) return;
    try {
      if (!localStorage.getItem(CONSENT_KEY)) {
        const id = setTimeout(() => setShow(true), 800);
        return () => clearTimeout(id);
      }
    } catch { /* ignored */ }
    return undefined;
  }, []);

  const decide = (v) => {
    try { localStorage.setItem(CONSENT_KEY, v); } catch { /* ignored */ }
    setShow(false);
  };

  if (!show) return null;
  return (
    <div
      role="dialog"
      aria-label="Préférences analytics"
      data-testid="analytics-consent-banner"
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-md z-[80] rounded-xl border border-slate-200 bg-white shadow-2xl p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-4 h-4 text-emerald-700" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900">Mesure d'audience interne — RGPD</p>
          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
            CONFILEX mesure son audience avec un outil interne, sans Google Analytics, sans cookie tiers.
            Les IP sont hashées avec un sel quotidien rotatif. Vous pouvez accepter, refuser ou tout effacer
            à tout moment depuis la <Link to="/legal" className="underline hover:text-slate-900">page Cookies</Link>.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              data-testid="consent-accept-btn"
              onClick={() => decide("accepted")}
              className="text-xs rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 font-medium"
            >
              Accepter
            </button>
            <button
              data-testid="consent-deny-btn"
              onClick={() => decide("denied")}
              className="text-xs rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 px-3 py-1.5"
            >
              Refuser
            </button>
          </div>
        </div>
        <button
          onClick={() => decide("denied")}
          aria-label="Fermer"
          className="text-slate-400 hover:text-slate-700"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
